const coldEmail = {
  sending: {
    status: {
      normal: 'Normal',
      warmup: 'Warming up',
      throttled: 'Throttled',
      paused: 'Paused',
      unavailable: 'Sending status unavailable',
    },
    provider: {
      workspace: 'Google Workspace',
      m365: 'Microsoft 365',
      gmail: 'Personal Gmail',
      other: 'Other business email',
    },
    dailyLimit: 'Daily limit',
    usage: 'Sent / limit in the rolling 24 hours',
    senderPool: 'Sending mailboxes',
    selectMailbox: 'Select at least one sending mailbox',
    saveLimit: 'Save limit',
    limitSaved: 'Mailbox daily limit updated',
    poolSaved: 'Sending mailboxes updated',
    missingMailbox:
      'A selected mailbox was deleted or is unavailable. Remove it and select an available mailbox.',
    removeMissingMailbox: 'Remove',
    repairPoolTitle: 'Update sender mailboxes',
    repairPoolConfirm:
      'These steps use senders outside the new pool: {steps}. Saving will switch them to automatic selection and preserve unsaved content edits. Stopped recipients will not resume automatically.',
    estimate: 'Estimated {days} days to finish',
    estimatePaused: 'Sending mailboxes are paused',
    estimateUnavailable: 'Estimate unavailable. Please try again later.',
    bind: {
      warmupTip:
        'New mailboxes need a 14-day warm-up to build reputation. Low volume during this period is expected.',
      gmailTip:
        'Personal Gmail suits low-volume testing. A Google Workspace custom domain delivers better.',
      primaryDomainTip:
        'If this is your company’s main website domain, consider a separate domain for cold email.',
      ok: 'Got it',
      title: 'Sending mailbox connected',
    },
  },
  title: 'Cold Outreach Campaigns',
  subtitle: 'Send personalized outreach from your connected email accounts',
  createNew: 'New Campaign',
  metricsAria: 'Campaign summary',
  metrics: {
    currentPageRecipients: 'Current page recipients',
    currentPageAccepted: 'Current page SMTP accepted',
    currentPageFailed: 'Current page failed',
    currentPageRunning: 'Current page running campaigns',
  },
  views: {
    list: 'Campaign List',
    edit: 'Edit Sequence',
    log: 'Send Logs',
    set: 'Outreach Settings',
  },
  sources: {
    tag: 'Talent Pool Tag',
    agent: 'Job Candidates · Agent',
    rec: 'Talent Recommendations',
  },
  sourceFilters: {
    selected: '{count} selected',

    advanced: 'Advanced options',
    connection: 'LinkedIn connection status',
    pipeline: 'Job candidate stage',
    unrestricted: 'Any status',
    hint: 'Select any matching statuses, or leave unrestricted. Applied only when importing.',
    matched: '{count} candidates match',
    countHint: 'Candidates without an email or excluded by deduplication rules will be skipped.',
    loading: 'Checking matching candidates…',
    failed: 'Could not check candidates. Please retry.',
    empty: 'No matching candidates. Adjust the source or statuses.',
    states: { Added: 'Connected', Pending: 'Pending', NotSent: 'Not added' },
    stages: {
      Submitted: 'Screening',
      Screening: 'Contacting',
      Recommended: 'Recommended',
      Interview: 'Interview',
      Offer: 'Offer negotiation',
      PreOnboarding: 'Awaiting onboarding',
      Onboarded: 'Onboarded',
    },
  },
  sequence: {
    panelTitle: 'Outreach Sequence',
    addStep: 'Add Email',
    clearSteps: 'Clear',
    emptySteps: 'No emails yet, click below to add one',
    saveBeforeStart: 'Save the email sequence before starting sends',
    saved: 'Saved',
    modified: 'Sequence modified',
    saveSequence: 'Save',
    noCampaignToSave: 'No outreach campaign is selected. Create a campaign before saving.',
    templates: 'Email Templates',
    switchTemplate: 'Switch Template',
    currentSequenceSaved: 'Current email sequence saved',
    unsavedChangesTitle: 'Unsaved Changes',
    unsavedChangesDesc:
      'You have unsaved changes. Leaving now will discard your modifications. Are you sure you want to leave?',
    continueEditing: 'Continue Editing',
    discardAndLeave: 'Discard & Leave',
    saveAndLeave: 'Save & Leave',
    inboxReplies: 'Inbox Replies',
    senderNoticeTitle: 'Sender Mailboxes',
    senderNoticeDesc: 'Bound {count} sender mailboxes, auto-rotating by quota',
    bindGmail: 'Connect Gmail',
    configSmtp: 'Configure SMTP',
    candidatePreviewTitle: 'Candidate Preview',
    candidatePreviewDesc: 'Preview variable substitution with real candidate data',
    candidateSearchPlaceholder: 'Search by name, title, or email',
    candidateListHeading: 'Preview candidates ({count})',
    candidatePreviewPosition: '{current} / {total}',
    previousCandidate: 'Previous candidate',
    nextCandidate: 'Next candidate',
    noCandidatesFound: 'No matching candidates',
    loadingCandidates: 'Loading candidates…',
    loadMoreCandidates: 'Load more candidates',
    noCandidatePreview: 'No candidates available for preview',
    candidateActions: 'Candidate actions',
    stopCandidate: 'Stop outreach for candidate',
    resumeCandidate: 'Resume outreach for candidate',
    removeCandidate: 'Remove candidate from campaign',
    removeCandidateConfirm: 'Remove "{name}" from this campaign?',
    candidateStoppedToast: 'Candidate outreach stopped',
    candidateResumedToast: 'Candidate outreach resumed',
    candidateRemovedToast: 'Candidate removed from campaign',
    aiPlaceholder:
      'Ask Agent to add, remove, or revise sequence steps, e.g. Add a follow-up after 5 days',
    stepNumber: 'Step {num}: {name}',
    stepPrefix: 'Step {num}: ',
    dayOf: 'Day {day}',
    waitDays: 'Wait {days} days',
    candidateCount: '{count} candidates',
    decreaseDays: 'Decrease wait days',
    increaseDays: 'Increase wait days',
    autoRotate: 'Auto-rotate ({count} mailboxes)',
    senderPicker: 'Sender',
    noSenderConfigured: 'No sender email configured',
    subject: 'Subject',
    modeAi: 'AI Written',
    modeManual: 'Manual',
    preview: 'Preview',
    previewAll: 'Preview all',
    editAll: 'Edit all',
    previewAllHint: 'First email and all follow-ups for the selected candidate',
    selectCandidatesBeforeStart:
      'Select a source and statuses and import candidates before sending',
    edit: 'Edit',
    aiNote:
      'AI will personalize this email based on candidate background before sending; below is base content',
    regen: 'Regenerate',
    loadTemplate: 'Load Template',
    editTemplate: 'Edit Template',
    templateEditing: 'Editing Template',
    templateEditingBanner: 'Editing "{name}". The current campaign will not be changed.',
    saveTemplate: 'Save Template',
    exitTemplateEdit: 'Exit Template Editing',
    systemTemplateReadOnly: 'System templates are read-only',
    templateNameRequired: 'Template name is required',
    templateSavedToast: 'Template "{name}" saved',
    templateEditRequiresSavedCampaign: 'Save the current campaign before editing a template',
    templateDiscardConfirm: 'This template has unsaved changes. Exit anyway?',
    templatePickerDescription: 'Selecting a template will replace the current sequence steps',
    loadingTemplates: 'Loading...',
    noTemplates: 'No templates available',
    templateStepCount: '{count} steps',
    createTemplate: 'New Template',
    createTemplateTitle: 'New Cold Email Template',
    createTemplateDescription:
      'Save the current sequence with {count} steps as a reusable template.',
    templateNameLabel: 'Template Name',
    templateNamePlaceholder: 'e.g. First outreach to AI engineer candidates',
    templateCreatedToast: 'Template "{name}" created',
    templateCreateFailed: 'Failed to create the template. Please try again.',
    templateRequiresSteps: 'Add at least one email step before creating a template.',
    deleteTemplate: 'Delete Template',
    deleteTemplateTitle: 'Delete Sequence Template',
    deleteTemplateConfirm: 'Delete template "{name}"? This cannot be undone.',
    templateDeletedToast: 'Template "{name}" deleted',
    templateLoadFailed: 'Failed to load templates. Please try again.',
    templateDeleteFailed: 'Failed to delete the template. Please try again.',
    templateSaveFailed: 'Failed to save the template. Please try again.',
    templateManagerTitle: 'Cold Email Templates',
    templateManagerListTitle: 'Template List',
    templateManagerDescription: 'Manage reusable multi-step cold outreach sequence templates.',
    templateManagerCreate: 'New Template',
    templateManagerCreateTitle: 'New Cold Email Template',
    templateManagerCreateDescription:
      'Create a template, then edit each step’s subject, body, and sending rules.',
    templateManagerEdit: 'Edit Cold Email Template',
    templateManagerReadOnly: 'View System Template',
    templateManagerSelect: 'Select a cold email template to start editing',
    templateManagerEmpty: 'No cold email templates yet',
    templateManagerName: 'Template Name',
    templateManagerSteps: 'Email Steps',
    templateManagerStepHint: 'Adjust the step order, wait days, and content of each email.',
    templateManagerAddStep: 'Add Step',
    templateManagerSave: 'Save Changes',
    templateManagerCancel: 'Discard Changes',
    templateManagerMoveUp: 'Move up',
    templateManagerMoveDown: 'Move down',
    templateManagerDiscardConfirm: 'This template has unsaved changes. Switch anyway?',
    templateStepNameLabel: 'Step {index} name',
    systemTemplate: 'System',
    customSender: 'Specific sender mailbox',
    waitDaysLabel: 'Wait days',
    bodyLabel: 'Email body',
    sendPreviewToMe: 'Send preview to my mailbox',
    sameThread: 'Same email thread',
    stopOnReply: 'Stop on candidate reply',
    deleteStep: 'Delete',
    undo: 'Undo',
    sequenceSavedToast: 'Sequence saved',
    stepDeletedToast: 'Deleted "{name}"',
    stepAddedToast: 'Added email step {num}',
    aiGeneratedToast: 'Added a new AI follow-up step',
    clearedToast: 'Cleared all email steps',
    orderAdjustedToast: 'Order updated, interval days preserved',
    previewSentToast: 'Preview of "{name}" sent to your mailbox',
    aiRegenToast: 'AI regenerated this email based on candidate profile',
    aiRewritePrompt: 'Rewrite {name} to make it more compelling and aligned with the role',
    aiRewriteFailed: 'AI rewrite failed',
    modeAiToast: 'This email will be personalized by AI before sending',
    modeManualToast: 'This email will be sent as written',
  },
  taskList: {
    filterAll: 'All',
    filterRunning: 'Running',
    filterPaused: 'Paused',
    filterDone: 'Completed',
    filterDraft: 'Draft',
    filterArchived: 'Archived',
    searchPlaceholder: 'Search campaigns / jobs / sources',
    noMatchingTasks: 'No matching campaigns found',
    colTask: 'Campaign',
    colSource: 'Source',
    colProgress: 'Progress',
    colReply: 'Replies',
    colStatus: 'Status',
    colCreated: 'Created',
    colAction: 'Actions',
    editSequence: 'Edit Sequence',
    stepCount: '{count} emails',
    toInbox: 'Go to Inbox',
    repliesCount: '{count} replies',
    sentProgress: 'Sent {sent} / {total}',
    failedCount: 'Failed {count}',
    dedupTip:
      'The same candidate will not be contacted by two active campaigns simultaneously: skipped on creation, with 90-day cooldown.',
    menuViewLogs: 'View Send Logs',
    menuCopyTask: 'Duplicate Campaign',
    menuSaveAsTpl: 'Save as Sequence Template',
    taskTemplateName: '{name} Template',
    menuToggleStatus: 'Pause / Resume Campaign',
    menuArchive: 'Archive Campaign',
    menuUnarchive: 'Unarchive Campaign',
    menuDelete: 'Delete Permanently',
    deleteTitle: 'Permanently Delete Campaign',
    deleteConfirm:
      'Delete "{name}" permanently? The campaign, steps, candidate queue, and send logs will be removed and cannot be recovered.',
    taskArchivedToast: 'Archived "{name}"',
    taskUnarchivedToast: 'Unarchived "{name}" as a draft',
    taskDeletedToast: 'Permanently deleted "{name}"',
    taskSavedTplToast: 'Saved {count} steps of "{name}" as template',
    taskToggledRunningToast: 'Campaign resumed',
    taskToggledPausedToast: 'Campaign paused, active candidates stopped at current step',
  },
  settings: {
    title: 'Outreach Settings',
    candidateSource: 'Candidate Sources',
    talentTag: 'Talent Tags',
    enrolledCount: 'Enrolled',
    enrolledDetail: 'Active {active} · Stopped {stopped}',
    enrolledPeople: '{count} people',
    senderAccountsTitle: 'Sender Mailboxes (Quota Rotation)',
    rateLimitValue: '{count} emails / {minutes} minutes',
    rateLimitLabel: 'Limit per sender mailbox',
    rateLimitScopeHint: 'This limit applies independently to each sender mailbox in this campaign.',
    saveRateLimit: 'Save limit',
    rateLimitSavedToast: 'Per-mailbox sending limit saved',
    noSenderAccounts: 'No sender email accounts connected',
    manageSenders: 'Manage Senders',
    stopRulesTitle: 'Stop Rules',
    ruleStopOnReply: 'Stop subsequent emails after candidate replies',
    ruleStopOnJobApplied: 'Stop after candidate is added to job',
    rulesSavedToast: 'Stop rules saved',
    readOnlyHint: 'Completed and archived tasks are read-only. Duplicate the task to make changes.',
  },
  logs: {
    title: 'Send Logs',
    totalSummary: 'Total {total} · Failed {failed}',
    colCandidate: 'Candidate',
    colStep: 'Email Step',
    colSenderEmail: 'Sender',
    colTime: 'Time',
    colStatus: 'Status',
    colAction: 'Actions',
    viewInInbox: 'View in Inbox',
    retry: 'Retry',
    retrySuccess: 'Re-enqueued for sending',
    loadMore: 'Load more logs',
    loadingMore: 'Loading…',
    statusSmtpOk: 'SMTP Accepted',
    statusFail: 'Failed',
    statusReply: 'Replied',
    statusQueued: 'Queued',
    allSenderEmails: 'All Sender Mailboxes',
  },
  dialogs: {
    newTaskTitle: 'Create Outreach Campaign',
    newTaskDesc:
      'Pick candidates from talent tags, job-matched candidates, or recommendations, and apply an outreach sequence.',
    taskNameLabel: 'Campaign Name',
    taskNamePlaceholder: 'e.g. Go Backend · Cold Outreach 09-16',
    candidateSourceLabel: 'Candidate Source',
    sourceOptionCount: '{name} ({count} candidates)',
    loading: 'Loading...',
    sourceLoadFailed: 'Could not load sources. Try again',
    selectCandidateSource: 'Select a candidate source',
    noAvailableSources: 'No sources available',
    sequenceTemplateLabel: 'Outreach Sequence',
    selectSequenceTemplate: 'Select a sequence template',
    blankSequence: 'Blank sequence',
    autoTaskNameSuffix: ' · Cold Outreach',
    dedupLabel: 'Deduplication',
    dedup90Days: 'Exclude candidates contacted in past 90 days',
    dedupActive: 'Exclude candidates in other active campaigns',
    cancel: 'Cancel',
    createAndEdit: 'Create and Edit Sequence',
    createdToast: 'Campaign created',
    createdAndEnrolledToast:
      'Campaign created, {enrolled} candidates enrolled, {skipped} skipped. Edit the sequence before starting.',
    noCandidatesEnrolledToast:
      'No candidates were enrolled into the campaign (not a campaign creation failure). Possible reasons include: missing resumes, no valid email address, 90-day deduplication, already enrolled in another active campaign, or already in this campaign.',
    enrollTitle: 'Enroll Candidates',
    enrollDesc:
      'Candidates start from email 1 and progress by interval days; stops automatically upon candidate reply.',
    enrollSourceLabel: 'Source',
    rateLimitLabel: 'Emails per mailbox / Time window',
    rateLimitUnitCount: 'emails /',
    rateLimitUnitMinutes: 'minutes',
    enrollAndStart: 'Enroll & Start Sending',
    enrolledToast: 'Candidates enrolled, step 1 emails scheduled for dispatch',
  },
  creator: {
    title: 'Create Campaign',
    talentTag: 'Talent Tag',
    selectTag: 'Select a tag',
    senderEmail: 'Sender Email',
    selectSenderEmail: 'Select sender emails',
    configureSenderEmail: 'Configure sender email',
    perMailboxLimit: 'Emails per mailbox',
    windowMinutes: 'Time window (minutes)',
    selectedSenderCount: '{selected} / {total} sender emails selected',
    rateLimitHint:
      'Each sender can send up to {count} emails within {window} minutes; extra emails wait for the next window.',
    emailContent: 'Email Content',
    useTemplate: 'Use Template',
    customContent: 'Custom Body',
    selectTemplate: 'Select an email template',
    subjectPlaceholder: 'Email subject',
    placeholderLabel: 'Variables',
    placeholderExample: 'Replaced with candidate data when sending',
  },
  placeholders: {
    name: 'Candidate name',
    role: 'Role',
    location: 'Location',
    summary: 'Profile summary',
  },
  recipients: {
    title: 'Recipients',
    previewSummary: '{candidates} candidates · {emails} emails',
    reload: 'Reload',
    selectRecommended: 'Recommended',
    selectAll: 'Select all eligible',
    clearSelection: 'Clear',
    emptySelectTag: 'Select a talent tag',
    unnamedCandidate: 'Unnamed candidate',
    previouslySent: 'Previously sent',
    createAndSend: 'Create Campaign and Start Sending',
  },
  counts: {
    emails: '{count} emails',
    previouslySent: '{count} previously sent',
  },
  pagination: '{total} total · Page {page} / {pages}',
  workspace: {
    campaignBoard: 'Campaign Board',
    sendLogs: 'Send Logs',
  },
  status: {
    all: 'All statuses',
    running: 'Running',
    paused: 'Paused',
    completed: 'Completed',
    archived: 'Archived',
    canceled: 'Canceled',
    pending: 'Pending',
    sending: 'Sending',
    waiting: 'Waiting',
    smtpAccepted: 'SMTP Accepted',
    failed: 'Failed',
    sendFailed: 'Send Failed',
    returned: 'Bounced',
    draft: 'Draft',
  },
  table: {
    task: 'Campaign',
    tagTask: 'Tag Campaign',
    status: 'Status',
    progress: 'Progress',
    progressSummary: '{sent} sent · {failed} failed · {pending} pending',
    senderEmail: 'Sender Email',
    recipientEmail: 'Recipient Email',
    estimatedFinish: 'Est. Finish',
    time: 'Time',
    subject: 'Subject',
    error: 'Error',
    candidate: 'Candidate',
    senderWindow: 'Window {used}/{limit}',
  },
  actions: {
    refresh: 'Refresh',
    retry: 'Retry',
    detail: 'Detail',
    pause: 'Pause',
    resume: 'Resume',
    startSending: 'Start Sending',
    cancel: 'Cancel',
    enrollCandidates: 'Enroll Candidates',
    outreachSettings: 'Outreach Settings',
    backToList: 'Outreach Campaigns',
  },
  detail: {
    title: 'Campaign Detail',
    total: 'Total',
    senderQuota: 'Sender Quota',
    availableNow: 'Available Now',
    nextAvailableAt: 'Next available at {time}',
    recipientDetails: 'Recipient Details',
  },
  remaining: {
    waitingSchedule: 'Waiting for dispatch',
    minutes: 'Approx. {minutes} min',
    hours: 'Approx. {hours} hr',
    hoursMinutes: 'Approx. {hours} hr {minutes} min',
  },
  messages: {
    created: 'Campaign created, {count} recipients enrolled',
    selectTaskFirst: 'Select a cold outreach campaign from the list first',
    loadFailed: 'Could not load the latest data.',
    actionFailed: 'That action failed. Please try again.',
    paused: 'Campaign paused',
    resumed: 'Campaign resumed',
    canceled: 'Campaign canceled',
  },
  duplicateConfirm: {
    title: 'Previously sent recipients selected',
    message: '{count} selected email(s) have already received a cold email. Send again?',
    confirm: 'Send again',
    back: 'Review selection',
  },
  cancelConfirm: {
    title: 'Cancel Campaign',
    message: 'Pending recipients will be canceled.',
    confirm: 'Confirm Cancel',
    back: 'Back',
  },
}

export default coldEmail
